import React, { useCallback, useEffect, useState } from "react";

import {
  assignPlanning, getPlanningTriage, updateOtherTask, updateTask,
} from "../../api";
import { MONTH_LABEL } from "./cellFormat";

// What is stuck, and why.
//
// The three buckets OVERLAP and are never summed. An undated task has no
// month AND no derivable estimate, so roughly 83 of the 98 no-estimate rows
// ARE the 83 unscheduled rows; adding the three would claim about 1,055 stuck
// items against a much smaller true union. Each bucket states its own count,
// the overlap is stated in words on the page, and there is no total anywhere.

const rowKeyOf = (row) => `${row.kind}-${row.id}`;

const BUCKETS = [
  {
    key: "overdue",
    label: "Overdue",
    why: "Open work whose planned month has already passed.",
  },
  {
    key: "unscheduled",
    label: "Unscheduled",
    why: "No date to plan from, so it sits in no month at all.",
  },
  {
    key: "no_estimate",
    label: "No estimate",
    why:
      "No prior-year actual and no peer median to derive hours from — " +
      "counted in the grid, but contributing nothing to its hours.",
  },
];

export default function TriageView({
  categories = [], monthOptions = [], employees = [], onChanged,
}) {
  const [counts, setCounts] = useState(null);
  const [openBucket, setOpenBucket] = useState(null);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [monthChoice, setMonthChoice] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Joined rather than the array itself: `categories` is a fresh array on
  // every parent render, and depending on it directly refetches forever.
  const catParam = categories.join(",");

  // One fetch path, used both by the effect and by a reload after a bulk
  // action. The effect passes a cancellation check so a slow response from a
  // bucket the manager has already closed cannot overwrite the current one.
  const load = useCallback((isCancelled = () => false) => {
    const params = {};
    if (catParam) params.categories = catParam;
    if (openBucket) params.bucket = openBucket;
    setError("");
    return getPlanningTriage(params)
      .then((data) => {
        if (isCancelled()) return;
        setCounts(data.counts);
        // Rows are opt-in: with no bucket expanded the server sends none, and
        // this must clear rather than keep showing the last bucket's list.
        setRows(openBucket ? data.rows || [] : null);
      })
      .catch(() => {
        if (isCancelled()) return;
        setError("Could not load the triage counts.");
      });
  }, [catParam, openBucket]);

  useEffect(() => {
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  const toggleBucket = (key) => {
    setSelected(new Set());
    setRows(null);
    setOpenBucket((prev) => (prev === key ? null : key));
  };

  const toggleRow = (row) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const key = rowKeyOf(row);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const chosenRows = () => (rows || []).filter((r) => selected.has(rowKeyOf(r)));

  // Routed by kind exactly as PlanningCellDrawer.handleMove does -- the same
  // problem solved a second, different way in a sibling file is the drift
  // this package exists to end.
  const handleMove = async () => {
    setBusy(true);
    setError("");
    try {
      await Promise.all(
        chosenRows().map((r) =>
          r.kind === "task"
            ? updateTask(r.id, { planned_period: monthChoice })
            : updateOtherTask(r.id, { planned_period: monthChoice })
        )
      );
      setSelected(new Set());
      await load();
      onChanged();
    } catch {
      // No onChanged on failure: telling the page to refetch would look like
      // the move worked.
      setError("Could not move those tasks.");
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async () => {
    setBusy(true);
    setError("");
    try {
      const chosen = chosenRows();
      await assignPlanning({
        task_ids: chosen.filter((r) => r.kind === "task").map((r) => r.id),
        other_task_ids: chosen
          .filter((r) => r.kind === "other-task")
          .map((r) => r.id),
        employee_id: assignee === "" ? null : parseInt(assignee, 10),
      });
      setSelected(new Set());
      await load();
      onChanged();
    } catch {
      setError("Could not assign those tasks.");
    } finally {
      setBusy(false);
    }
  };

  const nothingSelected = selected.size === 0;

  return (
    <div>
      <p style={{ color: "#6B7280", fontSize: 13, marginTop: 0 }}>
        Three ways work gets stuck. The buckets <strong>overlap</strong> — a
        task with no date has no estimate either — so the numbers are counted
        separately and never added up.
      </p>

      {error ? <p style={{ color: "#B91C1C" }}>{error}</p> : null}

      {BUCKETS.map((bucket) => {
        const open = openBucket === bucket.key;
        return (
          <section key={bucket.key} style={{ marginBottom: 12 }}>
            <button
              type="button"
              data-testid={`bucket-${bucket.key}`}
              onClick={() => toggleBucket(bucket.key)}
              aria-expanded={open}
              style={{
                width: "100%", textAlign: "left", padding: "10px 12px",
                border: "1px solid #E5E7EB", borderRadius: 6,
                background: open ? "#F9FAFB" : "#fff", cursor: "pointer",
                font: "inherit",
              }}
            >
              <span style={{ fontWeight: 600 }}>
                {counts ? counts[bucket.key] : "—"}
              </span>
              <span style={{ marginLeft: 8 }}>{bucket.label}</span>
              <div style={{ color: "#6B7280", fontSize: 12, marginTop: 2 }}>
                {bucket.why}
              </div>
            </button>

            {open ? (
              <div
                style={{
                  border: "1px solid #E5E7EB", borderTop: "none",
                  padding: "10px 12px",
                }}
              >
                {rows === null ? (
                  <p style={{ color: "#6B7280" }}>Loading…</p>
                ) : rows.length === 0 ? (
                  <p style={{ color: "#6B7280" }}>
                    Nothing in this bucket — nothing to unstick.
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {rows.map((row) => (
                      <li
                        key={rowKeyOf(row)}
                        style={{
                          display: "flex", gap: 10, alignItems: "flex-start",
                          padding: "8px 0", borderBottom: "1px solid #F3F4F6",
                        }}
                      >
                        <input
                          type="checkbox"
                          data-testid={`select-${rowKeyOf(row)}`}
                          checked={selected.has(rowKeyOf(row))}
                          onChange={() => toggleRow(row)}
                        />
                        <div>
                          <div>{row.name}</div>
                          <div style={{ color: "#6B7280", fontSize: 12 }}>
                            {row.client ? `${row.client} · ` : ""}
                            {row.category}
                            {row.due_date ? ` · due ${row.due_date}` : ""}
                            {row.status ? ` · ${row.status}` : ""}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {/* No "Set estimate" control anywhere: Task has no manual
                    estimate field -- hours are derived from prior-year
                    actuals and peer medians -- so a button here would have
                    nothing to write. The bucket is informational. */}
                <div
                  style={{
                    display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap",
                  }}
                >
                  <select
                    data-testid="triage-month"
                    value={monthChoice}
                    onChange={(e) => setMonthChoice(e.target.value)}
                  >
                    <option value="">Move to month…</option>
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>{MONTH_LABEL(m)}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleMove}
                    disabled={busy || nothingSelected || monthChoice === ""}
                  >
                    Set month
                  </button>

                  <select
                    data-testid="triage-assignee"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                  >
                    <option value="">Unassign</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAssign}
                    disabled={busy || nothingSelected}
                  >
                    Assign
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
